<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";

// Add search state

const activeFiltersCount = computed(() => {
  return (
    selectedCategories.value.length +
    selectedTags.value.length +
    selectedTypes.value.length +
    (showFeatured.value ? 1 : 0) +
    (showStreams.value ? 1 : 0)
  );
});

// Initialize refs properly
const tagSearch = ref("");
const titleSearch = ref("");

// Add drawer state
const isOpen = ref(false);

const showFloatingButton = ref(false);

// Add scroll watcher
onMounted(() => {
  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    showFloatingButton.value = scrollPosition > 200; // Show after 200px scroll
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });
});

// Add new search state
const typeSearch = ref("");

// Add search query ref and props
const searchQuery = ref("");

const props = defineProps<{
  categories: any[];
  tags: any[];
  types: any[];
  selectedCategories: number[];
  selectedTags: number[];
  selectedTypes: number[];
  showFeatured: boolean;
  showStreams: boolean;
  search: string; // Add this
  date: { from: string; to: string };
}>();

const emit = defineEmits<{
  (e: "update:selectedCategories", value: number[]): void;
  (e: "update:selectedTags", value: number[]): void;
  (e: "update:selectedTypes", value: number[]): void;
  (e: "update:showFeatured", value: boolean): void;
  (e: "update:showStreams", value: boolean): void;
  (e: "update:search", value: string): void; // Add this
  (e: "update:date", value: { from: string; to: string }): void;
  (e: "filter"): void;
}>();

// Add debounced search handler
const handleSearch = useDebounceFn(() => {
  emit("update:search", searchQuery.value);
  emit("filter");
}, 300);

// Update resetAllFilters
const resetAllFilters = () => {
  selectedCategories.value = [];
  selectedTags.value = [];
  selectedTypes.value = [];
  showFeatured.value = false;
  showStreams.value = false;
  tagSearch.value = "";
  typeSearch.value = "";
  searchQuery.value = ""; // Add this
  date.value = { from: "", to: "" };
  emit("update:search", ""); // Add this
  updateFilters();
};

const debouncedTitleSearch = useDebounceFn(() => {
  emit("titleSearch", titleSearch.value);
  emit("filter");
}, 300);

// Add computed property for types
const selectedTypes = computed({
  get: () => props.selectedTypes,
  set: (value) => emit("update:selectedTypes", value),
});

// Add computed property for filtered types
const filteredTypes = computed(() => {
  if (!typeSearch.value) return props.types;
  return props.types.filter((type) =>
    type.attributes.title.toLowerCase().includes(typeSearch.value.toLowerCase())
  );
});

// Update hasActiveFilters
const hasActiveFilters = computed(() => {
  return (
    selectedCategories.value.length > 0 ||
    selectedTags.value.length > 0 ||
    selectedTypes.value.length > 0 || // Add this line
    showFeatured.value ||
    showStreams.value ||
    titleSearch.value.length > 0 ||
    date.value.from ||
    date.value.to
  );
});

const selectedCategories = computed({
  get: () => props.selectedCategories,
  set: (value) => emit("update:selectedCategories", value),
});

const selectedTags = computed({
  get: () => props.selectedTags,
  set: (value) => emit("update:selectedTags", value),
});

const showFeatured = computed({
  get: () => props.showFeatured,
  set: (value) => emit("update:showFeatured", value),
});

const showStreams = computed({
  get: () => props.showStreams,
  set: (value) => emit("update:showStreams", value),
});

const date = computed({
  get: () => props.date,
  set: (value) => emit("update:date", value),
});

const updateFilters = () => {
  isOpen.value = false; // Close drawer
  emit("filter");
};

// Update selectCategory function and add selectType
const selectCategory = (categoryId: number) => {
  if (selectedCategories.value.includes(categoryId)) {
    emit("update:selectedCategories", []);
  } else {
    emit("update:selectedCategories", [categoryId]);
  }
  isOpen.value = false;
  emit("filter");
};

const selectType = (typeId: number) => {
  if (selectedTypes.value.includes(typeId)) {
    emit("update:selectedTypes", []);
  } else {
    emit("update:selectedTypes", [typeId]);
  }
  isOpen.value = false;
  emit("filter");
};
const filteredTags = computed(() => {
  if (!tagSearch.value) return props.tags;
  return props.tags.filter((tag) =>
    tag.attributes.title.toLowerCase().includes(tagSearch.value.toLowerCase())
  );
});

// Add computed property for filtered tags
// Add at the top of the script section with other computed properties
</script>

<template>
  <!-- Update the mobile filter buttons -->
  <button
    v-show="!showFloatingButton"
    @click="isOpen = true"
    class="md:hidden bg-primary text-white rounded-full px-5 py-3 flex items-center gap-2 hover:bg-primary/90 transition-all shadow-lg fixed bottom-20 right-4 z-[40]"
  >
    <Icon name="heroicons:funnel" class="w-5 h-5" />
    <span class="font-medium">{{
      hasActiveFilters ? `Filters` : "Filters"
    }}</span>
  </button>

  <button
    v-show="showFloatingButton"
    @click="isOpen = true"
    class="md:hidden fixed bottom-20 right-4 z-[40] bg-primary text-white rounded-full p-3.5 shadow-lg hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-2"
  >
    <Icon name="heroicons:funnel" class="w-5 h-5" />
  </button>

  <!-- Update the filters content wrapper -->

  <!-- Update the drawer structure -->
  <div
    :class="[
      'bg-white dark:bg-gray-900 z-[90] transition-all duration-300',
      'md:!translate-x-0 md:relative md:w-72 md:bg-transparent md:shadow-none',
      isOpen ? 'translate-x-0' : 'translate-x-full',
      'fixed inset-y-0 right-0 w-full sm:w-96 md:!static md:h-auto',
    ]"
  >
    <!-- Header -->
    <div
      class="sticky top-0 flex items-center justify-between p-4 md:hidden border-b dark:border-gray-800 bg-white dark:bg-gray-900 z-10"
    >
      <h2 class="text-lg font-bold flex items-center gap-2">
        <Icon name="heroicons:funnel" class="w-5 h-5 text-primary" />
        Filters
      </h2>
      <button
        @click="isOpen = false"
        class="p-2 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Icon name="heroicons:x-mark" class="w-6 h-6" />
      </button>
    </div>

    <!-- Scrollable Content Container -->
    <div class="h-[calc(100vh-64px)] md:h-auto overflow-y-auto">
      <div class="p-4 space-y-5">
        <!-- Search Input -->
        <div class="sticky top-0 z-10 dark:bg-gray-900 pt-2 pb-4">
          <div class="relative">
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch"
              placeholder="Search events..."
              class="w-full px-4 py-3 pl-10 text-base border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-800 dark:text-gray-200"
            />
            <Icon
              name="heroicons:magnifying-glass"
              class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />
          </div>
        </div>

        <!-- Categories -->
        <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <h3 class="font-semibold mb-3 flex items-center gap-2">
            <Icon name="heroicons:squares-2x2" class="w-4 h-4 text-primary" />
            Categories
          </h3>
          <div class="flex flex-col gap-2">
            <button
              v-for="category in categories"
              :key="category.id"
              class="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm transition-all hover:shadow-sm"
              :class="[
                selectedCategories.includes(category.id)
                  ? 'bg-primary/10 text-primary font-medium shadow-sm'
                  : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700',
              ]"
              @click="selectCategory(category.id)"
            >
              <img
                :src="category.attributes.icon.data.attributes.url"
                :alt="category.attributes.title"
                class="w-5 h-5 object-contain flex-shrink-0"
              />
              <span class="truncate">{{ category.attributes.title }}</span>
            </button>
          </div>
        </div>


        <!-- Dates -->
        <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <h3 class="font-semibold mb-3 flex items-center gap-2">
            <Icon name="heroicons:calendar-days" class="w-4 h-4 text-primary" />
            Dates
          </h3>
          <div class="space-y-3">
            <div>
              <label
                for="date-from"
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
                >From</label
              >
              <input
                id="date-from"
                type="date"
                v-model="date.from"
                @change="updateFilters"
                class="mt-1 w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label
                for="date-to"
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
                >To</label
              >
              <input
                id="date-to"
                type="date"
                v-model="date.to"
                @change="updateFilters"
                class="mt-1 w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-800 dark:text-gray-200"
              />
            </div>
          </div>
        </div>


        <!-- Tags -->
        <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <h3 class="font-semibold mb-3 flex items-center gap-2">
            <Icon name="heroicons:hashtag" class="w-4 h-4 text-primary" />
            Tags
          </h3>
          <div class="space-y-3">
            <div class="relative">
              <input
                v-model="tagSearch"
                type="text"
                placeholder="Search tags..."
                class="w-full px-3 py-2 pl-9 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-800 dark:text-gray-200"
              />
              <Icon
                name="heroicons:magnifying-glass"
                class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
            </div>
            <div
              class="space-y-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700"
            >
              <label
                v-for="tag in filteredTags"
                :key="tag.id"
                class="flex items-center p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer group"
              >
                <input
                  type="checkbox"
                  v-model="selectedTags"
                  :value="tag.id"
                  class="form-checkbox text-primary rounded border-gray-300 dark:border-gray-600"
                  @change="updateFilters"
                />
                <span
                  class="ml-2 text-sm group-hover:text-primary transition-colors"
                >
                  {{ tag.attributes.title }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Types -->
        <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <h3 class="font-semibold mb-3 flex items-center gap-2">
            <Icon name="heroicons:tag" class="w-4 h-4 text-primary" />
            Event Types
          </h3>
          <div class="flex flex-col gap-2">
            <button
              v-for="type in filteredTypes"
              :key="type.id"
              class="flex items-center gap-3 w-full p-2.5 rounded-lg text-sm transition-all"
              :class="[
                selectedTypes.includes(type.id)
                  ? 'bg-primary text-white font-medium shadow-sm'
                  : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700',
              ]"
              @click="selectType(type.id)"
            >
              <Icon
                :name="
                  type.attributes.icon || 'heroicons:magnifying-glass-circle'
                "
                class="w-5 h-5"
              />
              <span class="truncate">{{ type.attributes.title }}</span>
              <Icon
                v-if="selectedTypes.includes(type.id)"
                name="heroicons:check"
                class="w-4 h-4 ml-auto"
              />
            </button>
          </div>
        </div>

        <!-- Additional Filters -->
        <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 hidden">
          <h3 class="font-semibold mb-3 flex items-center gap-2">
            <Icon
              name="heroicons:adjustments-horizontal"
              class="w-4 h-4 text-primary"
            />
            Additional Filters
          </h3>
          <div class="space-y-3">
            <label
              class="flex items-center p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer group"
            >
              <input
                type="checkbox"
                v-model="showFeatured"
                class="form-checkbox text-primary rounded border-gray-300 dark:border-gray-600"
                @change="updateFilters"
              />
              <span
                class="ml-2 text-sm group-hover:text-primary transition-colors"
                >Verified Events</span
              >
            </label>
          </div>
        </div>


        <!-- Reset Button -->
        <button
          @click="resetAllFilters"
          class="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 active:bg-primary/95 rounded-lg transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          :disabled="!hasActiveFilters"
        >
          <Icon name="heroicons:arrow-path" class="w-4 h-4" />
          Reset Filters
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
